import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';

interface IAutoRequestViewContext {
  autoRequestIds: string[];
  setAutoRequestIds: (ids: string[]) => void;
}

const STORAGE_KEY = 'autoRequestViewIds';

export const AutoRequestViewContext = createContext<IAutoRequestViewContext>({
  autoRequestIds: [],
  setAutoRequestIds: () => {},
});

/**
 * Provider para gerenciar estado de autorequests.
 * 
 * Mantém uma lista de IDs de autorequests para monitoramento.
 * Os dados são persistidos no localStorage para sobreviver a navegações.
 */
export const AutoRequestViewProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [autoRequestIds, setAutoRequestIdsState] = useState<string[]>([]);

  /**
   * Carrega os IDs do localStorage ao montar o componente.
   */
  useEffect(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        if (Array.isArray(parsed)) {
          setAutoRequestIdsState(parsed);
        }
      }
    } catch (error) {
      console.error('Erro ao carregar autoRequestIds do localStorage:', error);
    }
  }, []);

  /**
   * Define os IDs e salva no localStorage.
   */
  const setAutoRequestIds = useCallback((ids: string[]) => {
    setAutoRequestIdsState(ids);
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(ids));
    } catch (error) {
      console.error('Erro ao salvar autoRequestIds no localStorage:', error);
    }
  }, []);

  return (
    <AutoRequestViewContext.Provider
      value={{
        autoRequestIds,
        setAutoRequestIds,
      }}
    >
      {children}
    </AutoRequestViewContext.Provider>
  );
};

/**
 * Hook para acessar o contexto de autorequests.
 * 
 * @returns Contexto de autorequests
 */
export const useAutoRequestViewContext = (): IAutoRequestViewContext => {
  const context = useContext(AutoRequestViewContext);
  if (!context) {
    throw new Error('useAutoRequestViewContext must be used within AutoRequestViewProvider');
  }
  return context;
};
